import { Component } from '@theme/component';

/**
 * Makes Shopify's collection filters feel like they are not reloading the page.
 *
 * The filtering itself is Shopify's, evaluated across the whole collection and
 * carried in the URL. This file used to *be* the filter — deriving materiaal,
 * doorkijk and stijl from the titles of whatever cards the page had rendered —
 * and that was wrong in a way that only showed up at scale: the page paginates
 * at 48, so on /collections/all it filtered 48 of 169 products and printed
 * counts to match. The attributes now live on the products as `awh.materiaal`,
 * `awh.doorkijk` and `awh.stijl`, derived once by the same logic and written to
 * Shopify, where Search & Discovery can count them properly.
 *
 * What is left is progressive enhancement, and nothing depends on it: every
 * chip is an ordinary link to a filtered URL, so the page works with the script
 * blocked, broken or still downloading. When it does run, the click is
 * intercepted, the section is re-rendered through the Section Rendering API,
 * and only the filter row, the grid and the pager are swapped.
 *
 * @extends {Component}
 */
class AwhCollection extends Component {
  #token = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPop);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('popstate', this.#onPop);
  }

  #onClick = (event) => {
    const link = event.target?.closest?.('[data-facet], .pager a');
    if (!link) return;
    // Let the browser have the ones it handles better than we would.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    const url = link.getAttribute('href');
    if (!url) return;
    history.pushState({}, '', url);
    this.#swap(url);
  };

  #onPop = () => this.#swap(window.location.href);

  async #swap(url) {
    const id = this.dataset.sectionId;
    if (!id) {
      window.location.href = url;
      return;
    }

    // Out-of-order responses: a fast second click must not be overwritten by a
    // slow first one.
    const mine = ++this.#token;
    this.classList.add('is-laden');

    try {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', id);
      const html = await fetch(target).then((r) => r.text());
      if (mine !== this.#token) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      for (const selector of ['.pfilter', '.prodgrid', '.pager']) {
        const next = doc.querySelector(selector);
        const current = this.querySelector(selector);
        if (next && current) current.replaceWith(next);
        else if (next && !current) this.querySelector('.awh-container')?.append(next);
        else if (!next && current) current.remove();
      }

      // Back to the top of the results, not the top of the document — the
      // header and the filters should stay where the eye left them.
      const grid = this.querySelector('.prodgrid');
      if (grid) {
        const scroller = document.querySelector('.page-wrapper') ?? document.scrollingElement;
        const top = grid.getBoundingClientRect().top;
        if (top < 0) scroller.scrollTop += top - 24;
      }
    } catch {
      // A failed swap must not strand the visitor on a stale grid.
      window.location.href = url;
    } finally {
      if (mine === this.#token) this.classList.remove('is-laden');
    }
  }
}

if (!customElements.get('awh-collection-component')) {
  customElements.define('awh-collection-component', AwhCollection);
}
